import { BookOpenCheck, Files, ScrollText } from 'lucide-react';
import { sonnatiTestimonial } from '@/content/testimonials';
import type { SolutionContent } from './types';

export const consulentiDelLavoroSolution: SolutionContent = {
  slug: 'consulenti-del-lavoro',
  navLabel: 'Consulenti del lavoro',
  navDescription: 'Il CCNL che applichi davvero, risolto in due scambi.',
  meta: {
    title: 'AI per consulenti del lavoro',
    description:
      'Affronta con precisione ogni tema di diritto del lavoro e previdenza. Riferimenti normativi, CCNL e interpretazioni già pronte per la pratica quotidiana.',
  },
  hero: {
    title: 'Trovanorma per consulenti del lavoro',
    subtitle:
      'Al tuo fianco quando arriva un dubbio sul CCNL, un inquadramento da verificare, un adempimento previdenziale da chiarire, un’assenza o un congedo da gestire.',
  },
  showcaseAudience: 'consulenti-del-lavoro',
  queryExamples: [
    {
      title: 'Costo di un’assunzione',
      icon: 'Calculator',
      question:
        'Quanto costa assumere un impiegato di IV livello commercio a **28.000 €** lordi? Costo azienda mese per mese, in un foglio di calcolo.',
      result: 'Foglio Excel del costo',
    },
    {
      title: 'Cedolino da verificare',
      icon: 'Receipt',
      question:
        'Allego il cedolino: controlla inquadramento, scatti e maggiorazioni rispetto al CCNL metalmeccanico industria.',
      attachment: 'cedolino-marzo.pdf',
      result: 'Verifica puntuale',
    },
    {
      title: 'Contestazione disciplinare',
      icon: 'FileWarning',
      question:
        'Assenza ingiustificata di **tre giorni**: preparami la lettera di contestazione a norma di CCNL e Statuto dei lavoratori.',
      result: 'Lettera pronta da inviare',
    },
    {
      title: 'Verbale ispettivo INL',
      icon: 'ClipboardList',
      question:
        'Allego il verbale dell’Ispettorato: quali rilievi sono fondati e come imposto la difesa? Mi serve entro venerdì.',
      attachment: 'verbale-ispettivo.pdf',
      result: 'Analisi e strategia',
    },
    {
      title: 'Fringe benefit auto',
      icon: 'Car',
      question:
        'Auto immatricolata a **marzo 2025** e assegnata a **giugno 2026**: quale criterio applico e quale importo entra in busta?',
      result: 'Risposta con le fonti',
    },
    {
      title: 'Rimborsi chilometrici',
      icon: 'Route',
      question:
        'Professionista in regime forfettario con rimborsi chilometrici: trattamento fiscale e che cosa scrivere in fattura.',
      result: 'Risposta con le fonti',
    },
  ],
  tasks: [
    {
      icon: BookOpenCheck,
      title: 'Quesiti di lavoro e previdenza',
      description:
        'Assunzioni, cessazioni, assenze, congedi, TFR, procedimenti disciplinari e previdenza. Approfondisci il caso con normativa, giurisprudenza e prassi degli enti competenti.',
    },
    {
      icon: ScrollText,
      title: 'CCNL, inquadramenti e trattamenti',
      description:
        'Verifica il contratto collettivo applicabile, inquadramenti, livelli, istituti contrattuali e accordi modificativi senza ricostruire il quadro su fonti separate.',
    },
    {
      icon: Files,
      title: 'Pareri e comunicazioni al cliente',
      description:
        'Trasforma l’approfondimento in un parere scritto, una comunicazione o un documento da condividere, adattando contenuto e registro al destinatario.',
    },
  ],
  comparison: {
    title: 'Non tutte le AI sono costruite per il lavoro giuslavoristico e previdenziale',
    text: 'Quando devi applicare normativa, prassi e CCNL a un caso concreto, devi poter verificare su cosa si basa la risposta.',
    genericLabel: 'AI generalista',
    drawbacks: [
      'Riferimenti a norme, CCNL e prassi non sempre presenti',
      'Aggiornamento delle fonti non garantito',
      'CCNL e prassi previdenziale trattati in un contesto generalista',
      'Analisi e comunicazioni da adattare al lavoro professionale',
      'Chat separata da documenti e calcoli',
    ],
    strengths: [
      'Riferimenti collegati ai documenti originali',
      'Fonti ufficiali sincronizzate ogni giorno',
      'Normativa, CCNL, prassi INPS e INAIL e giurisprudenza messi in relazione',
      'Pareri e comunicazioni già strutturati',
      'Dalla ricerca al documento e ai calcoli',
    ],
  },
  featuresTitle: 'Le funzionalità più utili nel lavoro giuslavoristico e previdenziale',
  featuredFeatures: [
    {
      slug: 'ricerca-e-quesiti',
      description:
        'Ricostruisci il quadro normativo, contrattuale e previdenziale e verifica i riferimenti utilizzati.',
    },
    {
      slug: 'pareri-e-memorandum',
      description:
        'Trasforma l’analisi in un documento strutturato da rivedere e condividere con il cliente.',
    },
    {
      slug: 'fogli-calcolo',
      description:
        'Costruisci prospetti e calcoli con formule modificabili, da continuare in Excel o ODS.',
    },
  ],
  cta: {
    title: 'Portaci il prossimo caso del tuo cliente',
    text: 'Un dubbio sul CCNL, un inquadramento, un calcolo o un quesito previdenziale: prova Trovanorma su qualcosa che devi risolvere davvero.',
  },
  testimonial: sonnatiTestimonial,
};
